import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiX } from 'react-icons/fi';

const NotificationOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const NotificationContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border-color);
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SuccessIcon = styled(IconContainer)`
  background-color: rgba(104, 211, 145, 0.1);
  color: var(--success-color);
`;

const ErrorIcon = styled(IconContainer)`
  background-color: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
`;

const WarningIcon = styled(IconContainer)`
  background-color: rgba(246, 224, 94, 0.1);
  color: var(--warning-dark);
`;

const NotificationTitle = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
`;

const NotificationMessage = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const getIcon = (type) => {
  switch (type) {
    case 'success':
      return <FiCheckCircle size={24} />;
    case 'error':
      return <FiXCircle size={24} />;
    case 'warning':
      return <FiAlertTriangle size={24} />;
    default:
      return <FiCheckCircle size={24} />;
  }
};

const getIconContainer = (type) => {
  switch (type) {
    case 'success':
      return SuccessIcon;
    case 'error':
      return ErrorIcon;
    case 'warning':
      return WarningIcon;
    default:
      return SuccessIcon;
  }
};

function Notification({ 
  isOpen, 
  onClose, 
  type = 'success', 
  title, 
  message, 
  buttonText = 'OK' 
}) {
  const IconContainer = getIconContainer(type);

  return (
    <AnimatePresence>
      {isOpen && (
        <NotificationOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <NotificationContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
            
            <NotificationHeader>
              <IconContainer>
                {getIcon(type)}
              </IconContainer>
              <NotificationTitle>{title}</NotificationTitle>
            </NotificationHeader>
            
            <NotificationMessage>{message}</NotificationMessage>
            
            <ActionButton onClick={onClose}>
              {buttonText}
            </ActionButton>
          </NotificationContent>
        </NotificationOverlay>
      )}
    </AnimatePresence>
  );
}

export default Notification;
